"use client"

import type { components } from "umbraco"
import Container from "../../Container"
import { Root, VideoWrapper, IframeWrapper, Caption } from "./styles"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type EmbeddedVideoData =
  components["schemas"]["EmbeddedVideoPropertiesModel"]

interface Props {
  data: EmbeddedVideoData
  settings?: Record<string, unknown> | null
  locale: string
  culture: string
  [key: string]: unknown
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function parseYouTubeUrl(raw: string): string | null {
  try {
    const url = new URL(raw)

    // youtube.com/watch?v=ID
    if (url.searchParams.has("v")) return url.searchParams.get("v")

    // youtu.be/ID
    if (url.hostname === "youtu.be") return url.pathname.slice(1) || null

    // youtube.com/embed/ID
    const embedMatch = url.pathname.match(/\/embed\/([\w-]+)/)
    if (embedMatch) return embedMatch[1]

    // youtube.com/v/ID
    const vMatch = url.pathname.match(/\/v\/([\w-]+)/)
    if (vMatch) return vMatch[1]
  } catch {
    // Not a valid URL
  }
  return null
}

function extractVideoId(input?: string | null): string | null {
  if (!input) return null
  const trimmed = input.trim()

  // Try as a full URL first
  const fromUrl = parseYouTubeUrl(trimmed)
  if (fromUrl) return fromUrl

  // Try with https:// prefix for protocol-less URLs (e.g. "youtube.com/watch?v=ID")
  if (/[./]/.test(trimmed)) {
    const fromPrefixed = parseYouTubeUrl(`https://${trimmed}`)
    if (fromPrefixed) return fromPrefixed
  }

  // Bare video ID — allow typical 11-char IDs (alphanumeric, underscore, hyphen)
  if (/^[\w-]{10,12}$/.test(trimmed)) return trimmed

  return null
}

function buildEmbedUrl(
  videoId: string,
  noCookie: boolean,
  params: string[],
): string {
  const domain = noCookie
    ? "www.youtube-nocookie.com"
    : "www.youtube.com"
  const query = params.length > 0 ? `?${params.join("&")}` : ""
  return `https://${domain}/embed/${videoId}${query}`
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function EmbeddedVideo({ data }: Props) {
  const videoId = extractVideoId(data.youtubeUrl)

  if (!videoId) return null

  const title = data.title || "Video"
  const layout = Array.isArray(data.layout) ? data.layout[0] : data.layout
  const isContained = layout === "contained"
  // EGDE-333: Privacy Enhanced Mode is not a decision editors need to make
  // ("not mandatory" per client). We always embed via the privacy-enhanced
  // youtube-nocookie.com domain (the recommended default) and ignore the
  // now-deprecated `privacyEnhanced` CMS toggle.
  const noCookie = true

  const params: string[] = ["rel=0"]
  if (data.autoplay) {
    params.push("autoplay=1")
    // Most mobile browsers require mute for autoplay to work
    params.push("mute=1")
  }
  if (data.controls === false) params.push("controls=0")
  if (data.startAt != null && data.startAt > 0)
    params.push(`start=${data.startAt}`)

  const embedUrl = buildEmbedUrl(videoId, noCookie, params)

  return (
    <Container paddingMobile>
      <Root data-block="embedded-video">
        <VideoWrapper $contained={isContained}>
          <IframeWrapper $contained={isContained}>
            <iframe
              src={embedUrl}
              title={title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              loading={data.lazyLoad !== false ? "lazy" : undefined}
              referrerPolicy="no-referrer-when-downgrade"
            />
          </IframeWrapper>
        </VideoWrapper>
        {data.caption && (
          <Caption variant="body2">{data.caption}</Caption>
        )}
      </Root>
    </Container>
  )
}
